import { Box, Button, IconButton, Stack, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import Switch from 'components/Common/Switch';
import TimePicker from 'components/Common/TimePicker';
import { get, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { MdOutlineContentCopy, MdAdd } from 'react-icons/md';
import Tooltip from 'components/Common/Tooltip';
import DataTable from 'components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

interface BusinessHoursProps {
  control: any;
  getValues: any;
  setValue: any;
  viewLabel?: boolean;
  fieldName?: string;
}

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' }
];

export default function BusinessHoursSection({
  control,
  viewLabel = true,
  getValues,
  setValue,
  fieldName = 'location.business_hours'
}: BusinessHoursProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    formState: { errors }
  } = useFormContext();
  const workingHoursError = get(errors, `${fieldName}.message`);

  const memoColumns = useMemo(
    () => columns({ control, fieldName, getValues, setValue }),
    [control, fieldName, getValues, setValue]
  );

  return (
    <Box mt={viewLabel ? 4 : 0}>
      <Typography variant='h6' mb={2} fontSize={16}>
        {viewLabel ? (fieldName?.includes('operator') ? 'Working Hours' : 'Business Hours') : ''}
      </Typography>

      {isMobile ? (
        <Stack spacing={2.5}>
          {daysOfWeek.map((day) => (
            <DayCard
              key={day.value}
              day={day}
              control={control}
              fieldName={fieldName}
              getValues={getValues}
              setValue={setValue}
            />
          ))}
        </Stack>
      ) : (
        <Box overflow='hidden'>
          <DataTable data={daysOfWeek} columns={memoColumns} isHeaderSticky={false} />
        </Box>
      )}

      {workingHoursError && (
        <Typography color='error' fontSize={12} sx={{ mt: 1 }}>
          {String(workingHoursError)}
        </Typography>
      )}
    </Box>
  );
}

const columns = (props: Omit<BusinessHoursProps, 'viewLabel'>): ColumnDef<any>[] => [
  {
    header: 'DAY',
    accessorKey: 'label',
    size: 100,
    cell: (info) => <Typography fontWeight={400}>{info.row.original.label}</Typography>
  },
  {
    header: 'STATUS',
    size: 100,
    cell: (info) => <Switch name={`${props.fieldName}.${info.row.original.value}.open`} control={props.control} />
  },
  {
    id: 'time',
    header: () => <Box textAlign='center'>TIME</Box>,
    cell: (info) => (
      <Box display='flex' gap={3}>
        <TimePicker name={`${props.fieldName}.${info.row.original.value}.from_time`} control={props.control} />
        <TimePicker name={`${props.fieldName}.${info.row.original.value}.to_time`} control={props.control} />
      </Box>
    )
  },
  {
    id: 'break',
    header: () => <Box textAlign='center'>BREAK</Box>,
    cell: (info) => <BreaksCell dayKey={info.row.original.value} {...props} isTable />
  },
  {
    header: 'ACTION',
    size: 100,
    cell: (info) => <CopyAction dayKey={info.row.original.value} {...props} />
  }
];

function DayCard({ day, ...props }: any) {
  const theme = useTheme();
  const isOpen = useWatch({ control: props.control, name: `${props.fieldName}.${day.value}.open` });

  return (
    <Paper
      variant='outlined'
      sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, borderColor: theme.palette.divider, transition: 'all 0.2s' }}
    >
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2.5}>
        <Typography fontWeight={600} fontSize={18}>
          {day.label}
        </Typography>
        <Box display='flex' alignItems='center' gap={1}>
          <Box display='flex' alignItems='center' gap={1}>
            <Switch name={`${props.fieldName}.${day.value}.open`} control={props.control} size='small' />
            <Typography fontSize={14} fontWeight={500} color={isOpen ? 'primary.main' : 'text.secondary'}>
              {isOpen ? 'Open' : 'Closed'}
            </Typography>
          </Box>
          <CopyAction dayKey={day.value} {...props} isMobile />
        </Box>
      </Box>

      {isOpen && (
        <Stack spacing={2}>
          <Box display='flex' gap={2}>
            <TimePicker name={`${props.fieldName}.${day.value}.from_time`} control={props.control} fullWidth />
            <TimePicker name={`${props.fieldName}.${day.value}.to_time`} control={props.control} fullWidth />
          </Box>
          <BreaksCell dayKey={day.value} {...props} />
        </Stack>
      )}
    </Paper>
  );
}

function BreaksCell({ dayKey, control, fieldName, isTable = false }: any) {
  const theme = useTheme();
  const fieldArrayName = `${fieldName}.${dayKey}.breaks`;
  const { fields, append, remove } = useFieldArray({ control, name: fieldArrayName as any });

  if (isTable) {
    return (
      <Box display='flex' alignItems='center' flexDirection='column'>
        <Box>
          {!fields.length && (
            <Button
              variant='outlined'
              size='small'
              onClick={() => append({ from: '', to: '' })}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                px: 2,
                py: 0.5,
                fontSize: 13,
                borderColor: '#E0E0E0',
                color: '#333',
                bgcolor: '#F9F9F9',
                '&:hover': { bgcolor: '#F1F1F1', borderColor: '#D6D6D6' }
              }}
            >
              Add Breaks
            </Button>
          )}
          {fields.map((f: any, idx: number) => (
            <Stack direction='row' spacing={1} alignItems='center' key={f.id} sx={{ mt: idx > 0 ? 1 : 0 }}>
              <TimePicker name={`${fieldArrayName}.${idx}.from` as any} control={control} />
              <TimePicker name={`${fieldArrayName}.${idx}.to` as any} control={control} />
              {idx === fields.length - 1 ? (
                <Tooltip title='Add Break' placement='top'>
                  <IconButton size='small' onClick={() => append({ from: '', to: '' })}>
                    <MdAdd size={24} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Box width={34} />
              )}
              <Tooltip title='Remove Break' placement='top'>
                <IconButton size='small' onClick={() => remove(idx)}>
                  <RiDeleteBin6Line size={24} color='#DA0808' />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box display='flex' flexDirection='column' gap={1.5}>
      {fields.map((f: any, idx: number) => (
        <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TimePicker name={`${fieldArrayName}.${idx}.from` as any} control={control} fullWidth />
          <TimePicker name={`${fieldArrayName}.${idx}.to` as any} control={control} fullWidth />
          <IconButton size='small' onClick={() => remove(idx)} sx={{ color: '#EF4444' }}>
            <RiDeleteBin6Line size={20} />
          </IconButton>
        </Box>
      ))}
      <Button
        variant='outlined'
        onClick={() => append({ from: '', to: '' })}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontSize: 14,
          fontWeight: 500,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          py: 1.2,
          width: '100%',
          '&:hover': { borderColor: theme.palette.primary.dark, bgcolor: 'rgba(37, 99, 235, 0.04)' }
        }}
      >
        + Add Breaks
      </Button>
    </Box>
  );
}

function CopyAction({ dayKey, fieldName, getValues, setValue, isMobile = false }: any) {
  const theme = useTheme();
  const handleCopy = () => {
    const source = getValues(`${fieldName}.${dayKey}`);
    daysOfWeek.forEach((day) => {
      const targetPath = `${fieldName}.${day.value}`;
      setValue(`${targetPath}.open`, source?.open);
      setValue(`${targetPath}.from_time`, source?.from_time);
      setValue(`${targetPath}.to_time`, source?.to_time);
      setValue(
        `${targetPath}.breaks`,
        (source?.breaks || []).map((b: any) => ({ from: b.from, to: b.to }))
      );
    });
  };

  return (
    <Tooltip title='Copy time to all' placement='top'>
      <IconButton
        onClick={handleCopy}
        size={isMobile ? 'small' : 'medium'}
        sx={isMobile ? { border: `1px solid ${theme.palette.divider}`, borderRadius: 2 } : {}}
      >
        <MdOutlineContentCopy size={isMobile ? 18 : 20} />
      </IconButton>
    </Tooltip>
  );
}
